/**
 * Outils › Messagerie – mise en forme d'un e-mail rédigé dans AGORA et des signatures PRODEVAL (gras, italique,
 * souligné, barré, listes, paragraphes, citations, liens, tableaux simples, images AGORA). Le HTML de l'éditeur (front)
 * est nettoyé par la MÊME fonction dans le navigateur et dans l'API avant l'envoi à Microsoft Graph : liste blanche de
 * balises ; attributs limités à `href` d'un lien (http, https, mailto), `style` (propriétés et valeurs en liste blanche :
 * couleur, police, taille, marges… ; ni url(), ni expression, ni échappement) et, pour une image, `src` (images hébergées
 * par AGORA seulement : /signature/<nom>.png ; aucune image extérieure, donc aucun pixel de suivi), `width`, `height`,
 * `alt`. Tout autre balisage est retiré en gardant son texte ; scripts, styles, cadres, objets… retirés avec leur
 * contenu ; commentaires retirés ; texte échappé ; balises refermées dans l'ordre.
 * Sans DOM (analyse par expressions régulières d'un balisage restreint) : utilisable par la Lambda.
 */

const BALISES = new Set([
  'p', 'div', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'a', 'blockquote', 'span', 'img',
  'table', 'tbody', 'tr', 'td',
]);
/** Balises retirées AVEC leur contenu (code, styles, objets, formulaires, métadonnées). */
const AVEC_CONTENU = new Set([
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'noscript', 'template', 'svg', 'math',
  'head', 'title', 'textarea', 'select', 'button', 'form', 'video', 'audio', 'canvas', 'xml',
]);
const VIDES = new Set(['br', 'img']);
const PROFONDEUR_MAX = 40;
const LIEN_AUTORISE = /^(https?:\/\/|mailto:)[^\s"'<>]*$/i;

/**
 * Images hébergées par AGORA (fichiers de web/public/signature) : chemin seul, l'hôte éventuel est ignoré ; préfixé par
 * l'adresse du front (`origineImages`) dans le HTML envoyé, pour que le destinataire les charge.
 */
const IMAGE_AGORA = /^(?:https?:\/\/[^/\s"'<>]+)?(\/signature\/[a-z0-9-]{1,60}\.png)$/i;
const PROPRIETES_STYLE = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'text-align',
  'line-height', 'letter-spacing', 'vertical-align', 'white-space', 'display', 'width', 'height', 'border',
  'border-collapse', 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
]);
/** Jetons admis dans une valeur de style (lus un par un, sans retour arrière) : couleurs, nombres, mots, polices. */
const JETON_STYLE = /\s*(?:#[0-9a-f]{3,8}|rgba?\([\d.%,\s]{5,40}\)|-?(?:\d+|\d*\.\d+)(?:px|pt|em|rem|%)?|[a-z][a-z-]*|"[\w -]{1,40}"|'[\w -]{1,40}'|,)\s*/iy;

const echapperTexte = (t: string) =>
  t.replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]{0,31}|#\d{1,7}|#x[0-9a-fA-F]{1,6});)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const echapperAttribut = (t: string) => t.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Entités usuelles et numériques d'une valeur d'attribut (pour la juger telle que le navigateur la lit). */
function decoderEntites(v: string): string {
  return v
    .replace(/&#x([0-9a-f]{1,6});?/gi, (_, h: string) => String.fromCodePoint(Math.min(parseInt(h, 16), 0x10ffff)))
    .replace(/&#(\d{1,7});?/g, (_, d: string) => String.fromCodePoint(Math.min(parseInt(d, 10), 0x10ffff)))
    .replace(/&(amp|quot|apos|lt|gt|colon|tab|newline);/gi, (_, n: string) => ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', colon: ':', tab: '\t', newline: '\n' })[n.toLowerCase() as 'amp']);
}

const MOTIFS_ATTRIBUT: Record<string, RegExp> = Object.fromEntries(['href', 'src', 'style', 'width', 'height', 'alt'].map((n) => [
  n, new RegExp(`(?:^|\\s)${n}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'=<>\`]+))`, 'i'),
]));

/** Valeur décodée d'un attribut (href, src, style, width, height, alt), ou null. */
function attributDe(attributs: string, nom: string): string | null {
  const m = MOTIFS_ATTRIBUT[nom]!.exec(attributs);
  return m ? decoderEntites(m[1] ?? m[2] ?? m[3] ?? '') : null;
}

/** Adresse d'un lien (attribut href) si elle est sûre, sinon null. */
function lienDe(attributs: string): string | null {
  const brute = (attributDe(attributs, 'href') ?? '').replace(/[\u0000- \u007f]+/g, '');
  return LIEN_AUTORISE.test(brute) ? brute : null;
}

/** Déclarations admises d'un attribut style (« propriété: valeur; … »), ou null. */
function styleDe(attributs: string): string | null {
  const brut = attributDe(attributs, 'style');
  if (!brut) return null;
  const admises = brut.split(';').flatMap((d) => {
    const i = d.indexOf(':');
    if (i < 0) return [];
    const propriete = d.slice(0, i).trim().toLowerCase();
    const valeur = d.slice(i + 1).trim();
    return PROPRIETES_STYLE.has(propriete) && valeur && valeur.length <= 120 && valeurStyleSure(valeur) ? [`${propriete}: ${valeur}`] : [];
  });
  return admises.length ? admises.join('; ') : null;
}

function valeurStyleSure(valeur: string): boolean {
  JETON_STYLE.lastIndex = 0;
  while (JETON_STYLE.lastIndex < valeur.length) {
    const avant = JETON_STYLE.lastIndex;
    if (!JETON_STYLE.exec(valeur) || JETON_STYLE.lastIndex === avant) return false;
  }
  return true;
}

/** Balise ouvrante nettoyée ; null : balise retirée (image hors d'AGORA). */
function ouverture(nom: string, attributs: string, origine: string): string | null {
  const style = styleDe(attributs);
  const s = style ? ` style="${echapperAttribut(style)}"` : '';
  if (nom === 'a') {
    const href = lienDe(attributs);
    return href ? `<a href="${echapperAttribut(href)}" target="_blank" rel="noopener noreferrer"${s}>` : `<a${s}>`;
  }
  if (nom === 'img') {
    const chemin = IMAGE_AGORA.exec((attributDe(attributs, 'src') ?? '').trim())?.[1];
    if (!chemin) return null;
    const dimensions = ['width', 'height'].map((a) => {
      const v = attributDe(attributs, a)?.trim() ?? '';
      return /^\d{1,3}$/.test(v) && Number(v) > 0 ? ` ${a}="${v}"` : '';
    }).join('');
    const alt = (attributDe(attributs, 'alt') ?? '').slice(0, 200);
    return `<img src="${echapperAttribut(origine + chemin.toLowerCase())}"${dimensions} alt="${echapperAttribut(alt)}"${s}>`;
  }
  return `<${nom}${s}>`;
}

/**
 * HTML de la rédaction ou d'une signature, sûr pour l'envoi et l'affichage. `origineImages` (API, à l'envoi) : adresse
 * du front qui sert les images AGORA (https://agora.prodeval.com) ; sans elle, chemins relatifs (aperçus du front).
 */
export function nettoyerHtmlRedaction(html: string, origineImages?: string | null): string {
  const origine = /^https?:\/\/[^/\s"'<>]+/i.exec(origineImages ?? '')?.[0] ?? '';
  const jeton = /<!--[\s\S]*?(?:-->|$)|<![^>]*>?|<\?[^>]*>?|<(\/?)([a-zA-Z][a-zA-Z0-9:-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>|<|[^<]+/g;
  const ouvertes: string[] = [];
  let sortie = '';
  let m: RegExpExecArray | null;
  while ((m = jeton.exec(html))) {
    const [brut, fermeture, nomBrut, attributs] = m;
    if (nomBrut === undefined) {
      // Texte (ou « < » isolé) ; commentaires, doctype et instructions retirés.
      if (brut.startsWith('<!') || brut.startsWith('<?')) continue;
      sortie += echapperTexte(brut);
      continue;
    }
    const nom = nomBrut.toLowerCase();
    if (AVEC_CONTENU.has(nom)) {
      if (!fermeture && !/\/\s*$/.test(attributs)) {
        // Saute jusqu'à la balise fermante correspondante (absente : tout le reste est retiré).
        const fin = new RegExp(`</${nom}\\s*>`, 'ig');
        fin.lastIndex = jeton.lastIndex;
        const f = fin.exec(html);
        jeton.lastIndex = f ? fin.lastIndex : html.length;
      }
      continue;
    }
    if (!BALISES.has(nom)) continue; // balise inconnue : retirée, son texte gardé
    if (VIDES.has(nom)) {
      if (!fermeture) sortie += nom === 'br' ? '<br>' : ouverture(nom, attributs, origine) ?? '';
      continue;
    }
    if (fermeture) {
      const i = ouvertes.lastIndexOf(nom);
      if (i === -1) continue;
      while (ouvertes.length > i) sortie += `</${ouvertes.pop()}>`;
      continue;
    }
    if (ouvertes.length >= PROFONDEUR_MAX) continue;
    sortie += ouverture(nom, attributs, origine) ?? `<${nom}>`;
    ouvertes.push(nom);
  }
  while (ouvertes.length) sortie += `</${ouvertes.pop()}>`;
  return sortie;
}

/** Texte brut d'un HTML de rédaction (aperçu, longueur) : balises retirées, entités usuelles décodées. */
export function texteDeHtmlRedaction(html: string): string {
  return decoderEntites(
    nettoyerHtmlRedaction(html)
      .replace(/<br>|<\/(p|div|li|blockquote|tr)>/g, '\n')
      .replace(/<[^>]+>/g, ''),
  ).replace(/\n{3,}/g, '\n\n').trim();
}
